import { onMounted, onUnmounted, watch, type Ref } from 'vue'
import { useBranding } from '@/composables/useBranding'
import { useBrandStore } from '@/stores/brand'
import { useCurrentHost } from '@/composables/useCurrentHost'
import { getAddressFormat } from '@/config/addressFormats'
import { FALLBACK_CURRENCY_CODE } from '@/config/regionCurrencies'

// SEO Meta Configuration
export interface SeoMeta {
  title?: string
  description?: string
  keywords?: string[]
  canonicalUrl?: string
  ogType?: 'website' | 'article' | 'product' | 'profile' | 'service'
  ogImage?: string
  ogImageAlt?: string
  noIndex?: boolean
  noFollow?: boolean
}

// JSON-LD Schema Types
export interface OrganizationSchema {
  '@type': 'Organization'
  name: string
  url: string
  logo: string
  description: string
  email?: string
  telephone?: string
  address?: {
    '@type': 'PostalAddress'
    addressCountry: string
  }
  sameAs?: string[]
}

export interface WebSiteSchema {
  '@type': 'WebSite'
  name: string
  url: string
  description: string
  potentialAction?: {
    '@type': 'SearchAction'
    target: string
    'query-input': string
  }
}

export interface LocalBusinessSchema {
  '@type': 'LocalBusiness' | 'ProfessionalService'
  name: string
  description: string
  url?: string
  image?: string
  telephone?: string
  email?: string
  address?: {
    '@type': 'PostalAddress'
    streetAddress?: string
    addressLocality?: string
    addressRegion?: string
    postalCode?: string
    addressCountry: string
  }
  geo?: {
    '@type': 'GeoCoordinates'
    latitude: number
    longitude: number
  }
  openingHoursSpecification?: Array<{
    '@type': 'OpeningHoursSpecification'
    dayOfWeek: string[]
    opens: string
    closes: string
  }>
  priceRange?: string
  aggregateRating?: {
    '@type': 'AggregateRating'
    ratingValue: number
    reviewCount: number
  }
}

export interface ServiceSchema {
  '@type': 'Service'
  name: string
  description: string
  provider?: {
    '@type': 'Organization' | 'Person'
    name: string
    url?: string
  }
  serviceType?: string
  areaServed?: string | { '@type': 'Country' | 'City'; name: string }
  offers?: {
    '@type': 'Offer'
    price?: string
    priceCurrency?: string
    availability?: string
  }
}

export interface FAQSchema {
  '@type': 'FAQPage'
  mainEntity: Array<{
    '@type': 'Question'
    name: string
    acceptedAnswer: {
      '@type': 'Answer'
      text: string
    }
  }>
}

export interface BreadcrumbSchema {
  '@type': 'BreadcrumbList'
  itemListElement: Array<{
    '@type': 'ListItem'
    position: number
    name: string
    item?: string
  }>
}

export interface ArticleSchema {
  '@type': 'Article'
  headline: string
  description?: string
  image?: string
  author?: { '@type': string; name: string }
  publisher?: { '@type': string; name: string; url?: string }
  datePublished?: string
  dateModified?: string
}

export interface ProductSchema {
  '@type': 'Product'
  name: string
  description?: string
  image?: string
  offers?: {
    '@type': 'Offer'
    price: string
    priceCurrency: string
    availability?: string
    seller?: { '@type': string; name: string }
  }
}

type JsonLdSchema =
  | OrganizationSchema
  | WebSiteSchema
  | LocalBusinessSchema
  | ServiceSchema
  | FAQSchema
  | BreadcrumbSchema
  | ArticleSchema
  | ProductSchema

// Dynamic brand-aware base URL.
//
// On a White-Label custom domain (e.g. wiseseagull.co.uk) we MUST emit
// canonicals and Open Graph URLs pointing at THAT host — not zaveato.co.uk —
// otherwise Google will consolidate ranking signals back into Zaveato and
// the shop's own domain never gets indexed independently. Canonical / WL
// classification lives in `useCurrentHost` (single source of truth); this
// module only asks the question, never re-parses the hostname.
function getBaseUrl() {
  if (typeof window !== 'undefined') {
    const { isWhiteLabel } = useCurrentHost()
    if (isWhiteLabel.value) {
      return `${window.location.protocol}//${window.location.host}`
    }
  }
  const { brand } = useBranding()
  return `https://www.${brand.value.domain}`
}
const DEFAULT_IMAGE_PATH = '/share/zaveato-share-dark-1200x630.png?v=4'

function getDefaultTitle() {
  const { brand } = useBranding()
  // On WL hosts, the default title must be the shop's brand — not the
  // Zaveato marketplace default (which mentions "Zaveato"). Prevents WL
  // leaks when a caller passes no title.
  const brandStore = useBrandStore()
  if (brandStore.isWhiteLabel && brandStore.brand?.shopName) {
    return brandStore.brand.shopName
  }
  return brand.value.seo.defaultTitle
}

const DEFAULT_DESCRIPTION =
  'Discover and order from your favourite local shops, plus book services with trusted UK businesses. Delivery or collection, appointments for healthcare, beauty, driving lessons and more.'

// Store original values to restore on unmount
let originalTitle = ''
let originalDescription = ''

// Track active JSON-LD scripts to clean up
const activeJsonLdScripts: HTMLScriptElement[] = []

// Canonical Zaveato social profiles — surfaced as Organization.sameAs so
// Google's Knowledge Graph ties these accounts to zaveato.co.uk. WL custom
// domains never hit getOrganizationSchema() (they publish their own inline
// Organization schema branded to the shop), so this stays canonical-only.
// Add YouTube + TikTok URLs to this list when those channels go live.
export const ZAVEATO_SOCIAL_URLS = [
  'https://www.facebook.com/Zaveato',
  'https://x.com/zaveato',
  'https://www.instagram.com/zaveato'
] as const

/**
 * Get the organization schema based on current brand.
 *
 * On a WL host, the Organization MUST be the shop — not Zaveato. Emitting
 * `name: "Zaveato"` on a WL domain tells Google the site's org identity is
 * Zaveato, which merges ranking signals back into canonical Zaveato and
 * strips the shop of its own entity. Also skip Zaveato's social handles
 * (`sameAs`) — the shop has no per-shop social URLs to advertise today.
 */
export function getOrganizationSchema(): OrganizationSchema {
  const { brand } = useBranding()
  const baseUrl = getBaseUrl()
  const brandStore = useBrandStore()

  if (brandStore.isWhiteLabel && brandStore.brand) {
    const shop = brandStore.brand
    return {
      '@type': 'Organization',
      name: shop.shopName,
      url: baseUrl,
      logo: shop.logoUrl || `${baseUrl}/favicon.ico`,
      description: shop.taglineText || shop.heroTagline || shop.shopName,
      email: brand.value.footer.email,   // support email stays canonical per WL scope rule
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'GB'
      }
      // sameAs deliberately omitted on WL — no per-shop social handles today
    }
  }

  return {
    '@type': 'Organization',
    name: brand.value.name,
    url: baseUrl,
    logo: `${baseUrl}/share/zaveato-share-dark-1200x630.png?v=4`,
    description: brand.value.meta.description,
    email: brand.value.footer.email,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'GB'
    },
    sameAs: [...ZAVEATO_SOCIAL_URLS]
  }
}

/**
 * Get the website schema with search action based on current brand.
 * WL hosts use the shop's own name — otherwise Google's SiteLinks Search
 * Box would attribute the site to Zaveato. Search target also stays under
 * the current baseUrl (WL host, not canonical).
 */
export function getWebSiteSchema(): WebSiteSchema {
  const { brand } = useBranding()
  const baseUrl = getBaseUrl()
  const brandStore = useBrandStore()

  const name = brandStore.isWhiteLabel && brandStore.brand?.shopName
    ? brandStore.brand.shopName
    : brand.value.name
  const description = brandStore.isWhiteLabel
    ? (brandStore.brand?.taglineText || brandStore.brand?.shopName || brand.value.meta.description)
    : brand.value.meta.description
  // WL shops have `/products?search=…`, not `/services?search=…` — the
  // services surface is canonical-only.
  const searchTarget = brandStore.isWhiteLabel
    ? `${baseUrl}/products?search={search_term_string}`
    : `${baseUrl}/services?search={search_term_string}`

  return {
    '@type': 'WebSite',
    name,
    url: baseUrl,
    description,
    potentialAction: {
      '@type': 'SearchAction',
      target: searchTarget,
      'query-input': 'required name=search_term_string'
    }
  }
}

/**
 * Create a service schema for a provider/service
 */
export function createServiceSchema(
  name: string,
  description: string,
  providerName: string,
  options?: {
    serviceType?: string
    price?: string
    priceCurrency?: string
    /** Country name the service is offered in (e.g. "United Kingdom", "Pakistan").
     *  Pass the provider's country from the shop/provider DTO; defaults to UK
     *  only when the caller doesn't have the info yet. */
    areaServedCountry?: string
  }
): ServiceSchema {
  const schema: ServiceSchema = {
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: providerName,
      url: getBaseUrl()
    },
    areaServed: {
      '@type': 'Country',
      // Fallback resolves via `getAddressFormat('GB').countryName` so a
      // future GB → new-region change automatically propagates (no
      // stray literal to grep for). Callers with shop context should
      // pass `areaServedCountry` explicitly.
      name: options?.areaServedCountry || getAddressFormat('GB').countryName
    }
  }

  if (options?.serviceType) {
    schema.serviceType = options.serviceType
  }

  if (options?.price) {
    schema.offers = {
      '@type': 'Offer',
      price: options.price,
      // Callers with a per-entity currency snapshot pass it in; fallback
      // routes through the shared constant.
      priceCurrency: options.priceCurrency || FALLBACK_CURRENCY_CODE,
      availability: 'https://schema.org/InStock'
    }
  }

  return schema
}

/**
 * Create a Product JSON-LD schema for a product detail page. Emits the
 * schema.org/Product graph Google needs for rich SERP snippets — price,
 * availability, brand, image. Star ratings + review count are optional
 * and only included when supplied.
 *
 * The `url` should be the CANONICAL page URL for this product (on WL
 * hosts, the shop's own domain; on canonical Zaveato, the marketplace
 * URL). Callers usually pass `window.location.href` after canonical
 * has stabilised, or explicitly build the WL host + product path.
 */
export function createProductSchema(
  name: string,
  description: string,
  url: string,
  options?: {
    image?: string | string[]
    sku?: string
    brand?: string
    price?: number | string
    priceCurrency?: string
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder' | 'Discontinued'
    rating?: { value: number; count: number }
    /** Shop name — populated on `offers.seller` so Google shows the seller
     *  next to the price in rich results. */
    seller?: string
  }
): Record<string, any> {
  const schema: Record<string, any> = {
    '@type': 'Product',
    name,
    description,
    url,
  }
  if (options?.image) schema.image = options.image
  if (options?.sku)   schema.sku = options.sku
  if (options?.brand) schema.brand = { '@type': 'Brand', name: options.brand }

  if (options?.price !== undefined) {
    schema.offers = {
      '@type': 'Offer',
      url,
      price: String(options.price),
      priceCurrency: options.priceCurrency || FALLBACK_CURRENCY_CODE,
      availability: `https://schema.org/${options.availability ?? 'InStock'}`,
    }
    if (options.seller) {
      (schema.offers as any).seller = { '@type': 'Organization', name: options.seller }
    }
  }

  if (options?.rating && options.rating.count > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: options.rating.value,
      reviewCount: options.rating.count,
    }
  }

  return schema
}

/**
 * Create a local business schema for a provider
 */
export function createLocalBusinessSchema(
  name: string,
  description: string,
  options?: {
    telephone?: string
    email?: string
    address?: {
      streetAddress?: string
      addressLocality?: string
      addressRegion?: string
      postalCode?: string
    }
    priceRange?: string
    rating?: { value: number; count: number }
  }
): LocalBusinessSchema {
  const schema: LocalBusinessSchema = {
    '@type': 'ProfessionalService',
    name,
    description,
    url: getBaseUrl(),
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'GB',
      ...options?.address
    }
  }

  if (options?.telephone) {
    schema.telephone = options.telephone
  }

  if (options?.email) {
    schema.email = options.email
  }

  if (options?.priceRange) {
    schema.priceRange = options.priceRange
  }

  if (options?.rating) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: options.rating.value,
      reviewCount: options.rating.count
    }
  }

  return schema
}

/**
 * Create an FAQ schema from Q&A pairs
 */
export function createFAQSchema(
  faqs: Array<{ question: string; answer: string }>
): FAQSchema {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }
}

/**
 * Create a breadcrumb schema from path items
 */
export function createBreadcrumbSchema(
  items: Array<{ name: string; url?: string }>
): BreadcrumbSchema {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url && index < items.length - 1 ? { item: item.url } : {})
    }))
  }
}

/**
 * Inject JSON-LD schema into the document head
 */
function injectJsonLd(schema: JsonLdSchema | JsonLdSchema[]): HTMLScriptElement {
  const script = document.createElement('script')
  script.type = 'application/ld+json'

  const wrappedSchema = Array.isArray(schema)
    ? { '@context': 'https://schema.org', '@graph': schema }
    : { '@context': 'https://schema.org', ...schema }

  script.textContent = JSON.stringify(wrappedSchema)
  document.head.appendChild(script)
  activeJsonLdScripts.push(script)
  return script
}

/**
 * Remove a JSON-LD script from the document
 */
function removeJsonLd(script: HTMLScriptElement): void {
  if (script.parentNode) {
    script.parentNode.removeChild(script)
  }
  const index = activeJsonLdScripts.indexOf(script)
  if (index > -1) {
    activeJsonLdScripts.splice(index, 1)
  }
}

/**
 * Update a meta tag or create it if it doesn't exist
 */
function setMetaTag(
  attribute: 'name' | 'property',
  value: string,
  content: string
): void {
  let meta = document.querySelector(
    `meta[${attribute}="${value}"]`
  ) as HTMLMetaElement | null

  if (meta) {
    meta.content = content
  } else {
    meta = document.createElement('meta')
    meta.setAttribute(attribute, value)
    meta.content = content
    document.head.appendChild(meta)
  }
}

/**
 * Update or create a link tag
 */
function setLinkTag(rel: string, href: string): HTMLLinkElement {
  let link = document.querySelector(
    `link[rel="${rel}"]`
  ) as HTMLLinkElement | null

  if (link) {
    link.href = href
  } else {
    link = document.createElement('link')
    link.rel = rel
    link.href = href
    document.head.appendChild(link)
  }

  return link
}

/**
 * Main SEO composable for managing page meta and structured data
 */
export function useSeo(meta?: SeoMeta | Ref<SeoMeta>) {
  let jsonLdScript: HTMLScriptElement | null = null

  const updateMeta = (seoMeta: SeoMeta) => {
    const { brand } = useBranding()
    // useBranding always returns canonical Zaveato — on WL custom domains we
    // need the shop's name as the brand suffix so titles don't say "Zaveato".
    // Also skip the " | {brand}" suffix when the title already starts with /
    // contains the brand name (typical on WL where the shop name is the lede).
    const brandStore = useBrandStore()
    const brandName = brandStore.isWhiteLabel
      ? (brandStore.brand?.shopName || brand.value.name)
      : brand.value.name

    // WL-safety scrub: any caller-supplied string that leaks "Zaveato" on a
    // WL host gets rewritten to the shop's brand. Covers config/seo.ts
    // templates ("… | Zaveato"), any hardcoded "on Zaveato", "Zaveato —",
    // etc. Word-boundary match; case-insensitive; no-op on canonical Zaveato.
    // Belt-and-braces so a future config edit can't leak Zaveato into a WL
    // title/description without CI noticing.
    const scrubZaveato = (s: string | undefined): string | undefined => {
      if (!s || !brandStore.isWhiteLabel) return s
      return s.replace(/\bZaveato\b/gi, brandName)
    }

    // Title
    let title: string
    if (seoMeta.title) {
      const cleanTitle = scrubZaveato(seoMeta.title)!
      const already = cleanTitle.toLowerCase().includes(brandName.toLowerCase())
      title = already ? cleanTitle : `${cleanTitle} | ${brandName}`
    } else {
      title = getDefaultTitle()
    }
    document.title = title

    // Description — scrubbed for WL. Same rationale as title.
    const description = scrubZaveato(seoMeta.description) || DEFAULT_DESCRIPTION
    setMetaTag('name', 'description', description)

    // Keywords
    if (seoMeta.keywords?.length) {
      setMetaTag('name', 'keywords', seoMeta.keywords.join(', '))
    }

    // Robots — always write the tag on every route change. Otherwise a
    // stale `noindex` from a prior page (Cart / Checkout / Account) can
    // leak into the next page's DOM on SPA navigation, and any indexable
    // page reached that way inherits noindex silently. Explicit "index,
    // follow" clears any lingering value.
    const robotsParts: string[] = []
    robotsParts.push(seoMeta.noIndex ? 'noindex' : 'index')
    robotsParts.push(seoMeta.noFollow ? 'nofollow' : 'follow')
    setMetaTag('name', 'robots', robotsParts.join(', '))

    // Canonical URL
    const baseUrl = getBaseUrl()
    const canonicalUrl =
      seoMeta.canonicalUrl || `${baseUrl}${window.location.pathname}`
    setLinkTag('canonical', canonicalUrl)

    // Open Graph
    setMetaTag('property', 'og:title', title)
    setMetaTag('property', 'og:description', description)
    setMetaTag('property', 'og:type', seoMeta.ogType || 'website')
    setMetaTag('property', 'og:url', canonicalUrl)
    // On WL hosts, preserve whatever og:image the server-side WlIndexHtmlRewriter
    // baked into the raw HTML rather than overwriting it with Zaveato's default
    // share image. Only override when the caller explicitly passes ogImage.
    // Was leaking https://<wl-host>/share/zaveato-share-dark-1200x630.png into
    // every WL /404 render → Google saw Zaveato branding on every URL.
    if (seoMeta.ogImage) {
      setMetaTag('property', 'og:image', seoMeta.ogImage)
    } else if (!brandStore.isWhiteLabel) {
      setMetaTag('property', 'og:image', `${baseUrl}${DEFAULT_IMAGE_PATH}`)
    }
    if (seoMeta.ogImageAlt) {
      setMetaTag('property', 'og:image:alt', seoMeta.ogImageAlt)
    }

    // Twitter — twitter:site tags the shared card. Never set @zaveato on a
    // WL host (leaks Zaveato branding to shop customers). If the shop
    // configures its own handle later, wire it through the brand store.
    if (!brandStore.isWhiteLabel) {
      setMetaTag('name', 'twitter:site', '@zaveato')
    }
    setMetaTag('name', 'twitter:title', title)
    setMetaTag('name', 'twitter:description', description)
    // Same rationale as og:image above — preserve the WL server-rendered value.
    if (seoMeta.ogImage) {
      setMetaTag('name', 'twitter:image', seoMeta.ogImage)
    } else if (!brandStore.isWhiteLabel) {
      setMetaTag('name', 'twitter:image', `${baseUrl}${DEFAULT_IMAGE_PATH}`)
    }
  }

  const setJsonLd = (schema: JsonLdSchema | JsonLdSchema[]) => {
    // Remove existing script if any
    if (jsonLdScript) {
      removeJsonLd(jsonLdScript)
    }
    jsonLdScript = injectJsonLd(schema)
  }

  const cleanup = () => {
    // Restore original title and description
    if (originalTitle) {
      document.title = originalTitle
    }
    if (originalDescription) {
      setMetaTag('name', 'description', originalDescription)
    }

    // Remove JSON-LD script
    if (jsonLdScript) {
      removeJsonLd(jsonLdScript)
      jsonLdScript = null
    }
  }

  onMounted(() => {
    // Store original values on first mount
    if (!originalTitle) {
      originalTitle = document.title
      const descMeta = document.querySelector('meta[name="description"]')
      originalDescription = descMeta?.getAttribute('content') || ''
    }

    // Apply initial meta if provided
    if (meta) {
      const metaValue = 'value' in meta ? meta.value : meta
      updateMeta(metaValue)
    }
  })

  onUnmounted(() => {
    cleanup()
  })

  // Watch for reactive changes
  if (meta && 'value' in meta) {
    watch(
      meta,
      (newMeta) => {
        if (newMeta) {
          updateMeta(newMeta)
        }
      },
      { deep: true }
    )
  }

  return {
    updateMeta,
    setJsonLd,
    // Schema builders
    getOrganizationSchema,
    getWebSiteSchema,
    createServiceSchema,
    createLocalBusinessSchema,
    createProductSchema,
    createFAQSchema,
    createBreadcrumbSchema
  }
}

/**
 * Inject base schemas for the homepage (Organization + WebSite)
 * Call this once in App.vue or main layout
 */
export function useBaseSeo() {
  let baseJsonLdScript: HTMLScriptElement | null = null

  onMounted(() => {
    // Inject organization and website schemas
    baseJsonLdScript = injectJsonLd([
      getOrganizationSchema(),
      getWebSiteSchema()
    ])
  })

  onUnmounted(() => {
    if (baseJsonLdScript) {
      removeJsonLd(baseJsonLdScript)
    }
  })
}

export default useSeo
